import { getCurrentLocale, normalizeLocale } from "./locales";

export type MessageParams = Record<string, string | number>;
type Messages = Record<string, Record<string, string>>;
const bundles = import.meta.glob<Messages>("./messages/*.json", { eager: true, import: "default" });
export const messages: Messages = {};
// Navigation terminology shared with Android wins over feature-local duplicates.
for (const [, bundle] of Object.entries(bundles).sort(([a], [b]) => Number(a.endsWith('/core.json')) - Number(b.endsWith('/core.json')))) {
  for (const [locale, values] of Object.entries(bundle)) Object.assign(messages[locale] ??= {}, values);
}

/** Plain text interpolation only: translated strings are never HTML or evaluated code. */
export function translate(source: string, params: MessageParams = {}, language: string = getCurrentLocale()): string {
  const locale = normalizeLocale(language);
  const translated = messages[locale]?.[source] ?? source;
  return translated.replace(/\{(\w+)\}/g, (token, key: string) => {
    const value = params[key];
    return value === undefined ? token : typeof value === "number" ? new Intl.NumberFormat(locale).format(value) : value;
  });
}

const errorSources: Record<string, string> = {
  STORAGE_UNAVAILABLE: "Allow this site to save browser data, then try signing in again.",
  SESSION_CHANGED: "Your account changed in another tab. Please try again.",
  REFRESH_BUSY: "Sign-in is being refreshed in another tab. Please retry.",
  REFRESH_TIMEOUT: "Sign-in refresh timed out. Please retry.",
  AUTHENTICATION_REQUIRED: "Sign in to continue.",
  RATE_LIMITED: "Too many requests. Please try again later.",
  GOOGLE_PLAY_NOT_CONFIGURED: "Purchases are not available yet.",
};
export function localizedError(cause: unknown, fallback: string, language: string = getCurrentLocale()): string {
  const locale = normalizeLocale(language);
  const error = cause as { message?: unknown; code?: unknown; status?: unknown } | null;
  const source = typeof error?.code === "string" ? errorSources[error.code] : undefined;
  if (source) return translate(source, {}, locale);
  const message = typeof error?.message === "string" ? error.message : "";
  if (locale === "en" && message) return message;
  if (message && messages[locale]?.[message]) return translate(message, {}, locale);
  // Errors originating in streaming helpers may already be translated.
  if (message && Object.values(messages[locale] ?? {}).includes(message)) return message;
  if (error?.status === 429) return translate("Too many requests. Please try again later.", {}, locale);
  return translate(fallback, {}, locale);
}

export function localizedDate(value: string | number | Date | null | undefined, options?: Intl.DateTimeFormatOptions, language: string = getCurrentLocale()): string {
  if (value === null || value === undefined || value === "") return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  const format = options ?? { year: "numeric", month: "short", day: "numeric" };
  // A release date is a calendar date, not midnight UTC in the viewer's time zone.
  const dateOnly = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  return new Intl.DateTimeFormat(normalizeLocale(language), dateOnly ? { ...format, timeZone: "UTC" } : format).format(date);
}
